/**
 * Step 2 — Schedule: the day in one line, then one row per question —
 * Starts · Every · Ends — the day strip that moves as they change, and each
 * drop's default amount, and where it comes from.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { describeRunDays, describeWeekendShare } from '@trafficpulse/shared';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { windowText } from '../../../../utils/clock/dk-label';
import { DayStrip } from '../../../shared/DayStrip';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import type { ContainerEditor } from '../../state/useContainerEditor';
import type { PreviousContainer } from '../hooks/use-general-times';
import { DaysRow } from './days/DaysRow';
import { DropDefaultsRows } from './DropDefaultsRows';
import { EndRow } from './EndRow';
import { EveryRow } from './EveryRow';
import { ScheduleHero } from './ScheduleHero';
import { StartRow } from './StartRow';
import { WindowRow } from './window/WindowRow';
import { ClockSeamNote } from './clock/ClockSeamNote';

export interface ScheduleStepProps {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  startMin: number | null;
  /** The end on the country's clock (the timing mirror's) — null = none. */
  endMin?: number | null;
  /** Drop minutes that go, in time order. */
  slotMinutes: number[];
  slotLabels: string[];
  spanEndMin: number | null;
  previous: PreviousContainer | null;
  /** Drops the end time cuts. */
  cut: number;
  /** Drops outside the country's window — not sent (a hard limit). */
  outside: number;
  /** Drops outside the window sent anyway (14/10/2026 — the container's or their own choice). */
  sentOutside: number;
  /** A route test: its drops go before the window on purpose — the row says so instead of offering the choice (20/10/2026). */
  routeTest?: boolean;
  /** Opens step 4 (Drop slots). */
  onOpenDrops: () => void;
}

export const ScheduleStep: FC<ScheduleStepProps> = ({ editor, plan, startMin, endMin = null, slotMinutes, slotLabels, spanEndMin, previous, cut, outside, sentOutside, routeTest, onOpenDrops }) => {
  const { draft } = editor;
  const clock = useCountryClock();
  const first = slotMinutes.length > 0 ? slotMinutes[0] : null;
  const linked = new Set(draft.groups.map((g) => g.listGroupId));
  const noGroup = draft.groups.length === 0 ? 0 : draft.slots.filter((s) => s.enabled && !s.listGroupIds.some((g) => linked.has(g))).length;
  const count = slotMinutes.length;
  return (
    <div className="flex flex-col gap-5">
      <ScheduleHero
        firstMin={first}
        lastMin={spanEndMin}
        count={count}
        noGroup={noGroup}
        outside={outside}
        windowLabel={windowText(plan.windowStartMin, plan.windowEndMin, clock)}
        every={draft.intervalMin}
        days={describeRunDays(draft.runDays)}
        weekend={describeWeekendShare(draft.runDays, draft.weekendAmountPct)}
        onOpenDrops={onOpenDrops}
      />
      <ClockSeamNote />
      <div className="divide-y divide-border/50">
        <StartRow editor={editor} plan={plan} startMin={startMin} previous={previous} />
        <EveryRow editor={editor} />
        <EndRow editor={editor} plan={plan} lastMin={spanEndMin} startMin={startMin} endMin={endMin} cut={cut} />
        <WindowRow editor={editor} plan={plan} outside={outside} sentOutside={sentOutside} routeTest={routeTest} />
        <DaysRow editor={editor} />
      </div>
      <DayStrip
        windowStartMin={plan.windowStartMin}
        windowEndMin={plan.windowEndMin}
        recipientTimezone={plan.recipientTimezone}
        spanStartMin={first}
        spanEndMin={spanEndMin}
        spanLabel={spanLabel(count, outside, draft.intervalMin)}
        slotMinutes={slotMinutes}
        slotLabels={slotLabels}
      />
      <div className="flex flex-col gap-1 border-t border-border/50 pt-4">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Each drop</span>
        <div className="divide-y divide-border/50">
          <DropDefaultsRows editor={editor} />
        </div>
      </div>
    </div>
  );
};

/** The day strip's caption: the drops that go, or why none do. */
function spanLabel(count: number, outside: number, every: number): string {
  if (count > 0) return `${count} drop ${count === 1 ? 'time' : 'times'} · every ${every} min`;
  return outside > 0 ? 'no drop inside the window' : 'add drops to see the day';
}
